/**
 * The cost requests that have been sent and may not have been answered (batch 8 T11, #3032): one cost
 * confirmation and one reconcile apply per signed-in session.
 *
 * A confirmation or an apply is a keyed command: the server binds the `Idempotency-Key` to the WHOLE request
 * and answers a repeat from its durable receipt. That only helps if the repeat is the same key with the same
 * payload, so the exact request is written down BEFORE it is sent and is kept until the server has decided:
 *
 *  - accepted (2xx, also a replay): cleared;
 *  - refused (400, 403, 404, 409 ... the server read the request and wrote nothing): cleared, the user
 *    corrects it and a new request gets a new key;
 *  - uncertain (network failure, timeout, 5xx, an unanswered earlier attempt): KEPT. The only thing the
 *    UI offers is to send the identical request again, or to discard it knowingly. It never sends the
 *    same intent under a fresh key, because if the first attempt committed that would confirm twice.
 *
 * Ownership. A record belongs to the session that wrote it (`CostingScope`: tenant, user and the session
 * family, never a token), and lives in a slot named after that scope. Another tenant, another user, a new
 * login or a signed-out page therefore cannot find it, and cannot overwrite or delete it: it is inert
 * (quarantined by its address) and is only ever resent by the session it belongs to, when the user asks.
 * A reload of the same session (also after a token rotation) finds it again with the exact key and payload.
 * Deleting is compare-and-delete: only the record that still holds the same key is removed, so an old
 * answer can never remove a newer intent.
 *
 * Records are stored in `sessionStorage` (per tab, gone with the tab). When the browser refuses storage the
 * caller is told, and the screen says truthfully that a reload cannot recover the request. A record of the
 * earlier, unscoped format (v1) names no owner, so it is never resent; it is only reported.
 */
import type { ConfirmItem, ConfirmRequest, CostConfirmationBasis } from '@/hooks/useInventoryCosting';
import { scopeId, type CostingScope } from './costingSession';

const PREFIX = 'inventory-costing-pending';
/** The unscoped record of the first release: reported, never resent. */
const LEGACY_CONFIRM_KEY = 'inventory-costing-pending-confirm:v1';

export type IntentKind = 'confirm' | 'reconcile';
export type PersistenceStatus = 'stored' | 'unavailable';

export interface ReconcileSelection {
  readonly partId: string;
  readonly warehouseId: string;
}

/** The one cost confirmation that was sent. */
export interface PendingConfirm {
  /** The `Idempotency-Key`: fixed for the life of this intent. */
  readonly key: string;
  readonly scope: CostingScope;
  /** The exact payload. Never rebuilt, never edited. */
  readonly request: ConfirmRequest;
  readonly startedAt: string;
}

/** The one reconcile apply that was sent. */
export interface PendingReconcile {
  readonly key: string;
  readonly scope: CostingScope;
  /** The exact selection. Never rebuilt, never edited. */
  readonly keys: readonly ReconcileSelection[];
  readonly startedAt: string;
}

export function newOperationKey(): string {
  const random =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
  return `cost-confirm-${random}`;
}

// --- shape validation ------------------------------------------------------------------------------

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);
const isText = (value: unknown, max: number): value is string => typeof value === 'string' && value.length > 0 && value.length <= max;
/** The bases the API accepts. Kept here (a test holds it equal to `COST_CONFIRMATION_BASES`) so reading a stored record needs nothing from the hooks module at run time. */
export const STORED_BASES: readonly CostConfirmationBasis[] = [
  'invoice_evidence',
  'physical_valuation',
  'supplier_price_selected',
  'last_receipt_selected',
  'catalog_cost_selected',
  'other',
];
const COST_TEXT = /^\d{1,12}(\.\d{1,4})?$/;
const MAX_ITEMS = 200;

function isConfirmItem(value: unknown): value is ConfirmItem {
  if (!isRecord(value)) return false;
  const { layerId, unitCostCents, basis, expectedConsumedQuantity, note, evidenceRef } = value;
  return (
    isText(layerId, 100) &&
    typeof unitCostCents === 'string' &&
    COST_TEXT.test(unitCostCents) &&
    typeof basis === 'string' &&
    (STORED_BASES as readonly string[]).includes(basis) &&
    typeof expectedConsumedQuantity === 'number' &&
    Number.isSafeInteger(expectedConsumedQuantity) &&
    expectedConsumedQuantity >= 0 &&
    (note === undefined || typeof note === 'string') &&
    (evidenceRef === undefined || typeof evidenceRef === 'string')
  );
}

export function isConfirmRequest(value: unknown): value is ConfirmRequest {
  if (!isRecord(value)) return false;
  const { items, acknowledgeConsumedQuantity } = value;
  if (!Array.isArray(items) || items.length === 0 || items.length > MAX_ITEMS) return false;
  if (typeof acknowledgeConsumedQuantity !== 'boolean') return false;
  const ids = new Set<string>();
  for (const item of items as unknown[]) {
    if (!isConfirmItem(item) || ids.has(item.layerId)) return false;
    ids.add(item.layerId);
  }
  return true;
}

function isReconcileKeys(value: unknown): value is ReconcileSelection[] {
  if (!Array.isArray(value) || value.length === 0 || value.length > MAX_ITEMS) return false;
  const seen = new Set<string>();
  for (const entry of value as unknown[]) {
    if (!isRecord(entry) || !isText(entry['partId'], 100) || !isText(entry['warehouseId'], 100)) return false;
    const id = `${entry['partId'].toLowerCase()}|${entry['warehouseId'].toLowerCase()}`;
    if (seen.has(id)) return false;
    seen.add(id);
  }
  return true;
}

function isScope(value: unknown): value is CostingScope {
  return isRecord(value) && isText(value['tenantId'], 200) && isText(value['userId'], 200) && isText(value['sessionFamilyId'], 200);
}

interface StoredRecord<P> {
  readonly v: 2;
  readonly kind: IntentKind;
  readonly key: string;
  readonly scope: CostingScope;
  readonly payload: P;
  readonly startedAt: string;
}

function parseRecord<P>(raw: string, kind: IntentKind, scope: CostingScope, validate: (payload: unknown) => payload is P): StoredRecord<P> | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(parsed)) return null;
  if (parsed['v'] !== 2 || parsed['kind'] !== kind) return null;
  if (!isText(parsed['key'], 200) || typeof parsed['startedAt'] !== 'string' || Number.isNaN(Date.parse(parsed['startedAt']))) return null;
  if (!isScope(parsed['scope']) || scopeId(parsed['scope']) !== scopeId(scope)) return null;
  if (!validate(parsed['payload'])) return null;
  return { v: 2, kind, key: parsed['key'], scope: parsed['scope'], payload: parsed['payload'], startedAt: parsed['startedAt'] };
}

// --- storage ---------------------------------------------------------------------------------------

const slotOf = (kind: IntentKind, scope: CostingScope): string =>
  `${PREFIX}:${kind}:v2:${[scope.tenantId, scope.userId, scope.sessionFamilyId].map(encodeURIComponent).join(':')}`;

function readSlot(slot: string): { raw: string | null; readable: boolean } {
  try {
    return { raw: window.sessionStorage.getItem(slot), readable: true };
  } catch {
    return { raw: null, readable: false };
  }
}

export type SaveStatus = PersistenceStatus | 'conflict';

function save<P>(kind: IntentKind, record: StoredRecord<P>, validate: (payload: unknown) => payload is P): SaveStatus {
  const slot = slotOf(kind, record.scope);
  const existing = readSlot(slot);
  if (!existing.readable) return 'unavailable';
  if (existing.raw !== null) {
    const other = parseRecord(existing.raw, kind, record.scope, validate);
    // A different intent of this very session is unresolved: it is never overwritten.
    if (other !== null && other.key !== record.key) return 'conflict';
  }
  try {
    const text = JSON.stringify(record);
    window.sessionStorage.setItem(slot, text);
    return window.sessionStorage.getItem(slot) === text ? 'stored' : 'unavailable';
  } catch {
    return 'unavailable';
  }
}

/** Removes the record only while it still holds `key`: an older answer never removes a newer intent. */
function clear(kind: IntentKind, scope: CostingScope, key: string): void {
  const slot = slotOf(kind, scope);
  try {
    const { raw } = readSlot(slot);
    if (raw === null) return;
    let stored: unknown;
    try {
      stored = JSON.parse(raw);
    } catch {
      return; // not a record of ours that we could name: left alone
    }
    if (isRecord(stored) && stored['key'] === key) window.sessionStorage.removeItem(slot);
  } catch {
    // Nothing was stored, so nothing is left behind.
  }
}

export interface Loaded<T> {
  readonly pending: T | null;
  /** `true` when something was stored for this session but unreadable, or is an unscoped old record: it was not used. */
  readonly discarded: boolean;
  /** `true` when the browser would not let the store be read: a pending request cannot be ruled out. */
  readonly unreadable: boolean;
}

function load<P>(kind: IntentKind, scope: CostingScope | null, validate: (payload: unknown) => payload is P): Loaded<StoredRecord<P>> {
  const none = { pending: null, discarded: false, unreadable: false } as const;
  if (scope === null) return none;
  const { raw, readable } = readSlot(slotOf(kind, scope));
  if (!readable) return { ...none, unreadable: true };
  if (raw === null) return none;
  const record = parseRecord(raw, kind, scope, validate);
  return record === null ? { ...none, discarded: true } : { ...none, pending: record };
}

/** Removes a damaged record of this session's own slot (the user dismissed the notice about it). */
function dropDamaged<P>(kind: IntentKind, scope: CostingScope, validate: (payload: unknown) => payload is P): void {
  const slot = slotOf(kind, scope);
  const { raw } = readSlot(slot);
  if (raw === null || parseRecord(raw, kind, scope, validate) !== null) return;
  try {
    window.sessionStorage.removeItem(slot);
  } catch {
    // Nothing more can be done.
  }
}

// --- confirmation ----------------------------------------------------------------------------------

const toConfirm = (record: StoredRecord<ConfirmRequest>): PendingConfirm => ({
  key: record.key,
  scope: record.scope,
  request: record.payload,
  startedAt: record.startedAt,
});

export function savePending(pending: PendingConfirm): SaveStatus {
  return save('confirm', { v: 2, kind: 'confirm', key: pending.key, scope: pending.scope, payload: pending.request, startedAt: pending.startedAt }, isConfirmRequest);
}

/** Compare-and-delete: only this exact intent (key and scope) is removed. */
export function clearPending(pending: Pick<PendingConfirm, 'key' | 'scope'>): void {
  clear('confirm', pending.scope, pending.key);
}

/** The stored confirmation of this session, if any. Another session's, a damaged or an unscoped record is never resent. */
export function loadPending(scope: CostingScope | null): Loaded<PendingConfirm> {
  const loaded = load('confirm', scope, isConfirmRequest);
  let legacy = false;
  try {
    legacy = scope !== null && window.sessionStorage.getItem(LEGACY_CONFIRM_KEY) !== null;
  } catch {
    // reported through `unreadable` above when the store cannot be read at all
  }
  return { pending: loaded.pending === null ? null : toConfirm(loaded.pending), discarded: loaded.discarded || legacy, unreadable: loaded.unreadable };
}

/** The user dismissed the notice about a record that could not be used. */
export function dropUnusablePending(scope: CostingScope): void {
  dropDamaged('confirm', scope, isConfirmRequest);
  try {
    window.sessionStorage.removeItem(LEGACY_CONFIRM_KEY);
  } catch {
    // Nothing more can be done.
  }
}

// --- reconcile ---------------------------------------------------------------------------------------

const toReconcile = (record: StoredRecord<ReconcileSelection[]>): PendingReconcile => ({
  key: record.key,
  scope: record.scope,
  keys: record.payload,
  startedAt: record.startedAt,
});

export function saveReconcile(pending: PendingReconcile): SaveStatus {
  return save('reconcile', { v: 2, kind: 'reconcile', key: pending.key, scope: pending.scope, payload: [...pending.keys], startedAt: pending.startedAt }, isReconcileKeys);
}

export function clearReconcile(pending: Pick<PendingReconcile, 'key' | 'scope'>): void {
  clear('reconcile', pending.scope, pending.key);
}

export function loadReconcile(scope: CostingScope | null): Loaded<PendingReconcile> {
  const loaded = load('reconcile', scope, isReconcileKeys);
  return { pending: loaded.pending === null ? null : toReconcile(loaded.pending), discarded: loaded.discarded, unreadable: loaded.unreadable };
}

export function dropUnusableReconcile(scope: CostingScope): void {
  dropDamaged('reconcile', scope, isReconcileKeys);
}

interface ErrorLike {
  status?: unknown;
  data?: unknown;
}

/**
 * Whether the server may have executed the request. A refusal that came back (4xx) was read and wrote
 * nothing; a transport failure, a 5xx, a timeout and the idempotency plugin's in-progress or unknown
 * outcome could be a commit whose answer was lost.
 */
export function isUncertainFailure(error: unknown): boolean {
  if (!(error instanceof Error)) return true;
  const { status, data } = error as ErrorLike;
  if (typeof status !== 'number') return true; // fetch rejected: no answer was read
  if (status === 408 || status >= 500 || status === 0) return true;
  const code = typeof data === 'object' && data !== null ? (data as { error?: unknown }).error : undefined;
  return typeof code === 'string' && code.startsWith('IDEMPOTENCY_');
}
